import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  Zap,
  Sparkles,
  Loader2,
  X,
  Tag,
  Clock,
  ShieldCheck,
  Flame,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/context/SubscriptionContext";
import { useAuth } from "@/context/AuthContext";

export default function Pricing() {
  const { user } = useAuth();
  const {
    subscription,
    openCheckout,
    checkoutBusy,
    loadingPlanId,
    plans,
    validateCoupon,
  } = useSubscription();

  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" | "3m" | "7d"
  const [couponInput, setCouponInput] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [showCouponInput, setShowCouponInput] = useState(false);

  const activePlanId = subscription?.active && !subscription?.is_trial ? subscription?.plan_id : null;

  // Filter plans based on the chosen cycle and sort by price
  const visiblePlans = plans
    .filter((p) => {
      if (billingCycle === "7d") return p.billing_cycle === "7d";
      if (billingCycle === "3m") return p.billing_cycle === "3m";
      return p.billing_cycle === "monthly";
    })
    .sort((a, b) => a.price_inr - b.price_inr);

  const handleApplyCoupon = async (targetPlanId = null) => {
    const code = couponInput.trim().toUpperCase();
    if (!code) {
      setCouponError("Please enter a promo code.");
      return;
    }
    setCouponLoading(true);
    setCouponError("");
    try {
      // Validate without locking to a single plan if targetPlanId not given
      const data = await validateCoupon(code, targetPlanId);
      setAppliedCoupon(data);
      setCouponError("");
    } catch (err) {
      setCouponError(err.message || "Invalid or expired promo code.");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError("");
  };

  const handleSubscribe = (plan) => {
    const isCouponEligible =
      appliedCoupon &&
      appliedCoupon.valid &&
      (!appliedCoupon.allowed_plans || appliedCoupon.allowed_plans.includes(plan.id));
    const couponToUse = isCouponEligible ? appliedCoupon.coupon_code : null;
    openCheckout(plan.id, plan.billing_cycle, couponToUse);
  };

  // Quota usage helper
  const getUsagePercent = (used, limit) => {
    if (!limit || limit <= 0) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  };

  return (
    <AppShell title="Plans & Billing">
      <div className="mx-auto max-w-6xl pb-20 px-4 sm:px-6">
        {/* Top Active Subscription & Quota Usage Overview */}
        {user && subscription && (
          <div className="mb-10 overflow-hidden rounded-2xl border border-border/80 bg-surface shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/70 px-6 py-4 bg-muted/20">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
                  <Zap className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-display text-base font-bold text-foreground">
                      {subscription.plan_name || "Free Starter Trial"}
                    </h2>
                    {subscription.is_admin ? (
                      <span className="rounded-full bg-purple-500/10 px-2 py-0.5 text-[11px] font-semibold text-purple-600">
                        Admin Unlimited
                      </span>
                    ) : subscription.is_trial ? (
                      <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-600">
                        Starter Trial
                      </span>
                    ) : (
                      <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">
                        Active Plan
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {subscription.is_admin
                      ? "Perpetual administrator access with unlimited generation."
                      : subscription.days_remaining > 0
                      ? `${subscription.days_remaining} day${subscription.days_remaining === 1 ? "" : "s"} remaining on this cycle.`
                      : subscription.is_trial
                      ? "Free quota to test out Virality before choosing a plan."
                      : "Subscription cycle ended. Choose a plan below to renew."}
                  </p>
                </div>
              </div>

              {!subscription.is_admin && (
                <div className="text-xs text-muted-foreground self-start sm:self-auto">
                  Status:{" "}
                  <span className="font-semibold text-foreground capitalize">
                    {subscription.billing_cycle || "Trial"}
                  </span>
                </div>
              )}
            </div>

            {/* Quota meters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6">
              {[
                {
                  label: "AI Content Ideas",
                  used: subscription?.usage?.ideas || 0,
                  limit: subscription?.quotas?.ideas || 0,
                },
                {
                  label: "Viral Hooks",
                  used: subscription?.usage?.hooks || 0,
                  limit: subscription?.quotas?.hooks || 0,
                },
                {
                  label: "Production Scripts",
                  used: subscription?.usage?.scripts || 0,
                  limit: subscription?.quotas?.scripts || 0,
                },
                {
                  label: "Hook Analyses",
                  used: subscription?.usage?.analysis || 0,
                  limit: subscription?.quotas?.analysis || 0,
                },
              ].map((item, idx) => {
                const isUnlimited = subscription.is_admin;
                const remaining = isUnlimited ? "∞" : Math.max(0, item.limit - item.used);
                const percent = isUnlimited ? 0 : getUsagePercent(item.used, item.limit);
                const isLow = !isUnlimited && remaining <= 2;

                return (
                  <div
                    key={idx}
                    className="rounded-xl border border-border/60 bg-background/50 p-3.5 flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-muted-foreground">{item.label}</span>
                        <span
                          className={`font-semibold ${
                            isLow ? "text-amber-600 font-bold" : "text-foreground"
                          }`}
                        >
                          {isUnlimited ? "Unlimited" : `${remaining} left`}
                        </span>
                      </div>
                      <div className="mt-2 flex items-baseline gap-1">
                        <span className="font-display text-xl font-bold text-foreground">
                          {isUnlimited ? "∞" : item.used}
                        </span>
                        {!isUnlimited && (
                          <span className="text-xs text-muted-foreground">/ {item.limit}</span>
                        )}
                      </div>
                    </div>
                    {!isUnlimited && (
                      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className={`h-full transition-all duration-300 ${
                            isLow ? "bg-amber-500" : "bg-accent"
                          }`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Header Title & Controls */}
        <div className="text-center pt-2">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Choose Your Plan
          </h1>
          <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">
            Scale your content output with high-retention hooks, viral scripts, and real-time intelligence.
          </p>

          {/* Billing Cycle Switcher */}
          <div className="mt-6 flex w-full max-w-sm sm:max-w-none sm:w-auto sm:inline-flex items-center justify-center mx-auto rounded-xl border border-border bg-surface p-1 text-xs sm:text-sm">
            <button
              onClick={() => {
                setBillingCycle("monthly");
              }}
              className={`flex-1 sm:flex-initial rounded-lg px-3 sm:px-5 py-2 font-medium transition-all ${
                billingCycle === "monthly"
                  ? "bg-accent !text-white shadow-sm font-bold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              style={billingCycle === "monthly" ? { color: "#ffffff" } : {}}
            >
              Monthly
            </button>
            <button
              onClick={() => {
                setBillingCycle("3m");
              }}
              className={`flex-1 sm:flex-initial flex items-center justify-center gap-1 sm:gap-1.5 rounded-lg px-2.5 sm:px-5 py-2 font-medium transition-all ${
                billingCycle === "3m"
                  ? "bg-accent !text-white shadow-sm font-bold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              style={billingCycle === "3m" ? { color: "#ffffff" } : {}}
            >
              <span style={billingCycle === "3m" ? { color: "#ffffff" } : {}}>3 Months</span>
              <span
                className={`rounded-full px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] font-bold ${
                  billingCycle === "3m"
                    ? "bg-white/20 !text-white"
                    : "bg-emerald-50 text-emerald-700"
                }`}
                style={billingCycle === "3m" ? { color: "#ffffff" } : {}}
              >
                Save 17%
              </span>
            </button>
            <button
              onClick={() => {
                setBillingCycle("7d");
              }}
              className={`flex-1 sm:flex-initial rounded-lg px-2.5 sm:px-5 py-2 font-medium transition-all ${
                billingCycle === "7d"
                  ? "bg-accent !text-white shadow-sm font-bold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              style={billingCycle === "7d" ? { color: "#ffffff" } : {}}
            >
              7-Day Pass
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div
          className={`mt-10 grid gap-6 ${
            visiblePlans.length === 1
              ? "mx-auto max-w-sm"
              : visiblePlans.length === 2
              ? "mx-auto max-w-3xl grid-cols-1 md:grid-cols-2"
              : "grid-cols-1 md:grid-cols-3"
          }`}
        >
          {visiblePlans.map((plan) => {
            const isCurrent = activePlanId === plan.id;
            const isPro = plan.id.includes("pro") && !plan.id.includes("super");
            const isSuperPro = plan.id.includes("super_pro");

            // Coupon calculation
            const isCouponEligible =
              appliedCoupon &&
              appliedCoupon.valid &&
              (!appliedCoupon.allowed_plans || appliedCoupon.allowed_plans.includes(plan.id));

            const hasDiscount = isCouponEligible;
            const discountAmount = hasDiscount
              ? appliedCoupon.fixed_price_inr !== undefined
                ? Math.max(0, plan.price_inr - appliedCoupon.fixed_price_inr)
                : Math.round(plan.price_inr * (appliedCoupon.discount_pct / 100))
              : 0;
            const finalPrice = hasDiscount
              ? appliedCoupon.fixed_price_inr !== undefined
                ? appliedCoupon.fixed_price_inr
                : Math.max(1, plan.price_inr - discountAmount)
              : plan.price_inr;

            const isPlanLoading = checkoutBusy && loadingPlanId === plan.id;

            // Plan features formatted cleanly
            const features = plan.features || [];

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col justify-between rounded-2xl border bg-card p-6 sm:p-7 transition-all ${
                  isPro
                    ? "border-accent shadow-md ring-1 ring-accent"
                    : isSuperPro
                    ? "border-purple-300 dark:border-purple-800 shadow-sm"
                    : "border-border shadow-sm hover:border-zinc-300"
                }`}
              >
                {/* Badge / Pill */}
                {isPro && (
                  <div className="absolute -top-3 left-6">
                    <span
                      className="rounded-full bg-accent px-3 py-0.5 text-[11px] font-semibold !text-white shadow-sm"
                      style={{ color: "#ffffff" }}
                    >
                      Most Popular
                    </span>
                  </div>
                )}
                {isSuperPro && (
                  <div className="absolute -top-3 left-6">
                    <span className="rounded-full bg-purple-600 px-3 py-0.5 text-[11px] font-semibold !text-white shadow-sm" style={{ color: "#ffffff" }}>
                      2x Power Tier
                    </span>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-xl font-bold text-foreground">
                      {plan.name.replace(/ \(.*?\)/, "")}
                    </h3>
                  </div>

                  <p className="mt-1.5 text-xs text-muted-foreground min-h-[32px] leading-relaxed">
                    {plan.tagline}
                  </p>

                  {/* Pricing Display */}
                  <div className="mt-5 border-b border-border/80 pb-5">
                    <div className="flex items-baseline gap-2">
                      {hasDiscount && (
                        <span className="font-display text-xl font-semibold text-muted-foreground line-through">
                          ₹{plan.price_inr}
                        </span>
                      )}
                      <span className="font-display text-4xl font-extrabold tracking-tight text-foreground">
                        ₹{finalPrice}
                      </span>
                      <span className="text-xs text-muted-foreground font-medium">
                        /{plan.duration_days === 7 ? "7 days" : plan.duration_days === 30 ? "month" : "3 months"}
                      </span>
                      {hasDiscount && (
                        <span className="ml-auto rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                          {appliedCoupon.fixed_price_inr !== undefined
                            ? "Special Offer"
                            : `${appliedCoupon.discount_pct}% OFF`}
                        </span>
                      )}
                    </div>
                    {plan.billing_cycle === "3m" && (
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Billed ₹{finalPrice} every 3 months (~₹{Math.round(finalPrice / 3)}/mo)
                      </p>
                    )}
                  </div>

                  {/* Included Quotas & Features */}
                  <div className="mt-6">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-3">
                      What's included:
                    </p>
                    <ul className="space-y-2.5">
                      {features.map((feat, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-2.5 text-xs text-foreground/90 sm:text-sm"
                        >
                          <Check
                            className={`h-4 w-4 shrink-0 mt-0.5 ${
                              isPro
                                ? "text-accent"
                                : isSuperPro
                                ? "text-purple-600"
                                : "text-muted-foreground"
                            }`}
                          />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Card CTA */}
                <div className="mt-8 pt-2">
                  {isCurrent ? (
                    <Button
                      disabled
                      className="w-full rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold text-xs py-2.5"
                    >
                      <Check className="mr-2 h-4 w-4 text-emerald-600" /> Current Plan
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleSubscribe(plan)}
                      disabled={checkoutBusy}
                      className={`w-full rounded-xl py-2.5 text-xs font-semibold transition-all !text-white shadow-sm ${
                        isPro
                          ? "bg-accent hover:bg-accent-hover"
                          : isSuperPro
                          ? "bg-purple-600 hover:bg-purple-700"
                          : "bg-zinc-900 hover:bg-zinc-800"
                      }`}
                      style={{ color: "#ffffff" }}
                    >
                      {isPlanLoading ? (
                        <>
                          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin text-white" />
                          <span className="text-white">Opening checkout…</span>
                        </>
                      ) : (
                        <span className="text-white">
                          Subscribe to {plan.name.replace(/ \(.*?\)/, "")}
                        </span>
                      )}
                    </Button>
                  )}
                  <p className="mt-2 text-center text-[11px] text-muted-foreground">
                    Instant access · Razorpay secure
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Promo Code Section (Below Cards) */}
        <div className="mt-8 flex flex-col items-center">
          {!showCouponInput && !appliedCoupon ? (
            <button
              onClick={() => setShowCouponInput(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground hover:underline"
            >
              <Tag className="h-3.5 w-3.5" />
              Have a promo code?
            </button>
          ) : appliedCoupon ? (
            <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/80 px-3.5 py-1.5 text-xs text-emerald-800 shadow-sm">
              <Tag className="h-3.5 w-3.5 text-emerald-600" />
              <span className="font-semibold">{appliedCoupon.coupon_code}</span>
              <span className="text-emerald-700">·</span>
              <span>
                {appliedCoupon.fixed_price_inr !== undefined
                  ? `Special price applied`
                  : `${appliedCoupon.discount_pct}% OFF applied`}
              </span>
              <button
                onClick={handleRemoveCoupon}
                className="ml-1 rounded p-0.5 hover:bg-emerald-200/60 transition-colors text-emerald-900"
                title="Remove promo code"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex w-full max-w-sm items-center gap-2">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value.toUpperCase());
                    setCouponError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleApplyCoupon();
                    }
                  }}
                  placeholder="Enter promo code"
                  className="w-full rounded-xl border border-border bg-background py-2 pl-9 pr-3 text-xs font-medium uppercase placeholder:normal-case placeholder:font-normal placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>
              <Button
                size="sm"
                onClick={() => handleApplyCoupon()}
                disabled={couponLoading || !couponInput.trim()}
                className="h-9 rounded-xl bg-zinc-900 px-4 text-xs font-semibold !text-white hover:bg-zinc-800"
                style={{ color: "#ffffff" }}
              >
                {couponLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-white" />
                ) : (
                  <span className="text-white">Apply</span>
                )}
              </Button>
              <button
                onClick={() => {
                  setShowCouponInput(false);
                  setCouponError("");
                }}
                className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {couponError && (
            <p className="mt-2 text-xs font-medium text-destructive">{couponError}</p>
          )}
        </div>

        {/* Security & Payment Footer Note */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground border-t border-border/60 pt-6">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <span>Secured with Razorpay (UPI, Cards, NetBanking)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>Instant quota activation &amp; priority generation</span>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
